import React, { useState, useEffect, memo } from 'react';
import { fromJS, List, Map } from 'immutable';
import PropTypes from 'prop-types';

import { InputProps } from '../../helpers/AutoComplete';
import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import Select from '../../helpers/Select';
import Checkbox from '../../helpers/Checkbox';
import FormGroup from '../../helpers/FormGroup';

import style from './style.module.css';

/**
 * Run windward report popup.
 *
 * @param {object} props Props.
 * @param {object} props.initialValues Initial values.
 * @param {object} props.firstDealMaker FirstDealMaker suggests element.
 * @param {boolean} props.isDisabledRunButton Whether submit button is disabled or not.
 * @param {object} props.module Module suggests element.
 * @param {Function} props.onCheckboxFieldUpdate Handle checkbox's field update.
 * @param {Function} props.onClose Handle popup close.
 * @param {Function} props.onRadioFieldUpdate Handle radio button's field update.
 * @param {Function} props.onRunWindwardReport Handle popup submit.
 * @param {Function} props.onOpenSaveQueryPopup Handle popup save query.
 * @param {Function} props.onSelectReport Handle select report type.
 * @param {boolean} props.popupLoading Whether popup is loading or not.
 * @param {boolean} props.researchAnalyst ResearchAnalyst suggests element.
 * @param {object} props.secondDealMaker SecondDealMaker suggests element.
 * @param {string} props.selectedTemplateType Selected template type.
 * @param {List} props.templates Template types list.
 *
 */
const RunWindwardReportPopup = props => {
  const [radioFieldsValues, setRadioFieldsValues] = useState(new Map());

  useEffect(() => {
    setInitRadioFieldsValues(selectedTemplate);
  }, [props.selectedTemplateType]);

  const isLoading = Boolean(props.popupLoading || props.initialValues);
  const header = 'Run Windward Report';
  const footer = (
    <div className="flex-grid">
      {props.onOpenSaveQueryPopup ? (
        <Button
          key="saveQuery"
          disabled={props.isDisabledRunButton}
          onClick={props.onOpenSaveQueryPopup}
        >
          Save Query
        </Button>
      ) : (
        <div />
      )}
      <div>
        <Button
          key="run"
          className="btn-primary"
          disabled={props.isDisabledRunButton}
          onClick={props.onRunWindwardReport}
        >
          Run
        </Button>
        <Button key="close" onClick={props.onClose}>
          Close
        </Button>
      </div>
    </div>
  );

  const templates = props.templates.map(template =>
    Map({
      name: template.get('title'),
      value: template.get('name'),
    }),
  );

  const selectedTemplate = props.templates.filter(
    template => fromJS(props.selectedTemplateType) === template.get('name'),
  );

  const formContent = selectedTemplate?.map(template => {
    const checkboxFields = template.get('checkboxFields');
    const suggestedFields = template.get('suggestedFields');
    const radioFields = template.get('radioFields');
    let checkboxContent = null;
    let suggestedContent = null;
    let radioContent = null;

    if (checkboxFields?.size > 0) {
      checkboxContent = checkboxFields.map(field => {
        const defaultChecked = props.initialValues
          ?.get('templateParams')
          .toJS()
          .find(template => template.field === field.get('name'));

        return (
          <Checkbox
            key={field.get('name')}
            defaultChecked={defaultChecked}
            label={field.get('title')}
            name={field.get('name')}
            onChange={props.onCheckboxFieldUpdate}
          />
        );
      });
    }

    if (suggestedFields?.size > 0) {
      const sortedFields = suggestedFields.sortBy(field => field.get('title'));

      suggestedContent = sortedFields.map(field => (
        <InputProps key={field.get('name')} label={field.get('title')} name={field.get('name')}>
          {getSuggest(field.get('name'))}
        </InputProps>
      ));
    }

    if (radioFields?.size > 0) {
      radioContent = radioFields.map(field => {
        const radioButtons = field.get('values').map(item => {
          const defaultChecked = props.initialValues
            ?.get('templateParams')
            .toJS()
            .find(template => template.field === item.get('name'))?.field;

          const key = field.get('name');
          const value = radioFieldsValues.get(key);

          return (
            <div key={item.get('name')} className="radio-wrap radio-wrap--reverse pr16">
              <label htmlFor={item.get('name')}>{item.get('title')}</label>
              <input
                checked={defaultChecked || value === item.get('name')}
                id={item.get('name')}
                name={field.get('name')}
                onChange={event => onRadioFieldUpdate(event, key)}
                type="radio"
                value={item.get('name')}
              />
            </div>
          );
        });

        return (
          <FormGroup key={field.get('name')} className="d-flex flex-wrap">
            {radioButtons}
          </FormGroup>
        );
      });
    }

    return (
      <div key={template.get('name')} className={style.formContent}>
        {checkboxContent}
        {suggestedContent}
        {radioContent}
      </div>
    );
  });

  function setInitRadioFieldsValues(selectedTemplate) {
    const radioFields = selectedTemplate.get(0)?.get('radioFields');

    if (radioFields?.size > 0) {
      radioFields.forEach(field => {
        const key = field.get('name');
        const initValue = field
          .get('values')
          .get(0)
          .get('name');

        setRadioFieldsValues(radioFieldsValues.set(key, initValue));
        props.onRadioFieldUpdate({
          target: {
            name: key,
            value: initValue,
          },
        });
      });
    }
  }

  function onRadioFieldUpdate(event, fieldKey) {
    setRadioFieldsValues(radioFieldsValues.set(fieldKey, event.target.value));
    props.onRadioFieldUpdate(event);
  }

  function getSuggest(name) {
    switch (name) {
      case 'firstDealMaker':
        return props.firstDealMaker;

      case 'secondDealMaker':
        return props.secondDealMaker;

      case 'researchAnalyst':
        return props.researchAnalyst;

      default:
        return null;
    }
  }

  return (
    <Popup footer={footer} header={header} id="runWindwardReportPopup" loading={isLoading}>
      <div className="row">
        <div className="col-xs-6">
          <InputProps label="Module" name="module">
            {props.module}
          </InputProps>
        </div>
        <FormGroup className="col-xs-6">
          <Select
            id="dropdownType"
            label="Report"
            name="windwardReportType"
            onChange={props.onSelectReport}
            options={templates}
            value={props.selectedTemplateType}
            showLabel
          />
        </FormGroup>
      </div>
      <div className={style.fields}>
        <p className="form-section-label">Optional Fields</p>
        <form>{formContent}</form>
      </div>
    </Popup>
  );
};

RunWindwardReportPopup.propTypes = {
  firstDealMaker: PropTypes.instanceOf(Object).isRequired,
  isDisabledRunButton: PropTypes.bool.isRequired,
  module: PropTypes.instanceOf(Object).isRequired,
  onCheckboxFieldUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenSaveQueryPopup: PropTypes.func,
  onRadioFieldUpdate: PropTypes.func.isRequired,
  onRunWindwardReport: PropTypes.func.isRequired,
  onSelectReport: PropTypes.func.isRequired,
  popupLoading: PropTypes.bool.isRequired,
  secondDealMaker: PropTypes.instanceOf(Object).isRequired,
  selectedTemplateType: PropTypes.string.isRequired,
  templates: PropTypes.instanceOf(List).isRequired,
};

export default memo(RunWindwardReportPopup);
